import type { SerializeFrom } from '~/types/serialize-from';
import { useCallback, useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';
import { atom, useAtom } from 'jotai';
import {
    RECENTLY_VIEWED_ACTION,
    type loader,
} from '~/routes/actions.recently-viewed';
import { useIsGoodBot } from '~/modules/root';

const recentlyViewedAtom = atom<
    NonNullable<SerializeFrom<typeof loader>>['recentlyViewedItems'] | null
>(null);

/**
 * Hook to get the current user's recently viewed items.
 */
export function useRecentlyViewed() {
    const isFetched = useRef(false); // Fetch only once per component.
    const isLoaded = useRef(false);
    const fetcher = useFetcher<typeof loader>({ key: 'recently-viewed' });
    const isBot = useIsGoodBot();
    const [recentlyViewed, setRecentlyViewed] = useAtom(recentlyViewedAtom);

    useEffect(() => {
        if (isBot || isLoaded.current) {
            return;
        }
        if (fetcher.state === 'idle' && !isFetched.current) {
            fetcher.load(RECENTLY_VIEWED_ACTION);
            isFetched.current = true;
        } else if (fetcher.data) {
            setRecentlyViewed(fetcher.data.recentlyViewedItems);
            isFetched.current = true;
            isLoaded.current = true;
        }
    }, [fetcher, isBot, setRecentlyViewed]);

    return {
        recentlyViewed: recentlyViewed ?? [],
        refresh: useCallback(() => {
            fetcher.load(RECENTLY_VIEWED_ACTION);
            isFetched.current = true;
            isLoaded.current = false;
        }, [fetcher]),
    };
}
